import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { CircularProgress, FormControl, MenuItem, Select } from "@mui/material";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { HttpService } from "../../../service/HttpService";
import FullScreenLoader from "../../../components/Loader/FullScreenLoader";
import { useAuth } from "../../../context/AuthProvider";
import moment from "moment";
import "./RockwoolRootingHistory.scss";

const FILTER_STORAGE_KEY = "rockwoolRootingHistoryDateFilter";

const ChartCustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null;
  const { cultivar, days_from_cut, average_weight_loss } =
    payload[0].payload || {};
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: 4,
        padding: 12,
      }}
    >
      <div>
        <strong>Cultivar Name : </strong> {cultivar ?? "-"}
      </div>
      <div>
        <strong>No. of Days from Cut Date : </strong> {days_from_cut ?? "-"}
      </div>
      <div>
        <strong>Average Weight Loss : </strong> {average_weight_loss ?? "-"}
      </div>
    </div>
  );
};

const RockwoolRootingHistory = () => {
  const { setShowLoginPopup, showLoginPopup } = useAuth();
  const httpService = useMemo(
    () => new HttpService(setShowLoginPopup),
    [setShowLoginPopup]
  );

  const storedFilters = JSON.parse(
    localStorage.getItem(FILTER_STORAGE_KEY) || "null"
  );

  const hasValidStoredStart =
    storedFilters?.startDate && dayjs(storedFilters.startDate).isValid();
  const hasValidStoredEnd =
    storedFilters?.endDate && dayjs(storedFilters.endDate).isValid();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("cluster");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(15);
  const [historyRowCount, setHistoryRowCount] = useState(0);
  const [historySearch, setHistorySearch] = useState("");
  const [selectedPlantId, setSelectedPlantId] = useState(null);
  const [selectedDaysFromCut, setSelectedDaysFromCut] = useState(null);
  const [pendingStartDate, setPendingStartDate] = useState("");
  const [pendingEndDate, setPendingEndDate] = useState("");

  const isMountedRef = useRef(false);
  const historySectionRef = useRef(null);

  const scrollToHistorySection = useCallback(() => {
    if (historySectionRef.current) {
      historySectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      startDate &&
      endDate &&
      dayjs(startDate).isValid() &&
      dayjs(endDate).isValid() &&
      dayjs(endDate).isBefore(dayjs(startDate), "day")
    ) {
      const clearedEnd = "";
      setEndDate(clearedEnd);
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ startDate, endDate: clearedEnd })
      );
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
  }, []);

  const fetchRootingHistory = async () => {
    setLoading(true);
    try {
      const trimmedSearch = search.trim();
      let url =
        `rockwool/rockwool-average-weight-loss` +
        `?page=${page + 1}&pageSize=${pageSize}` +
        `${trimmedSearch ? `&search=${trimmedSearch}` : ""}` +
        `${typeFilter ? `&type=${typeFilter}` : ""}`;

      const hasValidStart = !!startDate && dayjs(startDate).isValid();
      const hasValidEnd = !!endDate && dayjs(endDate).isValid();

      if (hasValidStart && hasValidEnd) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      } else {
        const today = dayjs().format("YYYY-MM-DD");
        const novStartDate = "2025-11-01"
        url += `&start_date=${novStartDate}&end_date=${today}`;
      }

      const res = await httpService.get(url);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const total =
        res?.data?.pagination?.total ||
        res?.data?.meta?.total ||
        list.length ||
        0;

      if (!isMountedRef.current) return;
      setRows(list);
      setRowCount(total);
    } catch (_) {
      if (!isMountedRef.current) return;
      setRows([]);
      setRowCount(0);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRootingHistory();
  }, [
    page,
    pageSize,
    startDate,
    endDate,
    search,
    typeFilter,
    httpService,
    showLoginPopup,
  ]);

  const fetchHistoryDetails = async () => {
    setHistoryLoading(true);
    try {
      const trimmedSearch = historySearch.trim();
      let url =
        `rockwool/cluster-clone-details` +
        `?page=${historyPage + 1}&pageSize=${historyPageSize}` +
        `${trimmedSearch ? `&search=${trimmedSearch}` : ""}` +
        `${typeFilter ? `&type=${typeFilter}` : ""}`;

      if (selectedPlantId && !trimmedSearch) {
        url += `&plant_id=${selectedPlantId}`;
      }
      if (selectedDaysFromCut && !trimmedSearch) {
        url += `&days_from_cut=${selectedDaysFromCut}`;
      }

      const hasValidStart =
        !trimmedSearch && !!startDate && dayjs(startDate).isValid();
      const hasValidEnd =
        !trimmedSearch && !!endDate && dayjs(endDate).isValid();

      if (hasValidStart && hasValidEnd) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      } else {
        const today = dayjs().format("YYYY-MM-DD");
        const novStartDate = "2025-11-01"
        url += `&start_date=${novStartDate}&end_date=${today}`;
      }

      const res = await httpService.get(url);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const total =
        res?.data?.pagination?.total ||
        res?.data?.meta?.total ||
        list.length ||
        0;

      if (!isMountedRef.current) return;
      setHistoryRows(list);
      setHistoryRowCount(total);
    } catch (_) {
      if (!isMountedRef.current) return;
      setHistoryRows([]);
      setHistoryRowCount(0);
    } finally {
      if (!isMountedRef.current) return;
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoryDetails();
  }, [
    historyPage,
    historyPageSize,
    historySearch,
    selectedPlantId,
    startDate,
    endDate,
    httpService,
    showLoginPopup,
    typeFilter,
    selectedDaysFromCut,
  ]);

  // const chartData = useMemo(() => {
  //   const grouped = new Map();
  //   console.log("rows", rows);
  //   rows.forEach((row) => {
  //     const plantId =
  //       row?.plant_id || row?.clone_id || row?.id || row?._id || row?.cultivar;
  //     if (!plantId) return;

  //     const existing = grouped.get(plantId) || {
  //       plantId,
  //       cultivar: row?.cultivar || "-",
  //       totalDays: 0,
  //       count: 0,
  //       average_weight_loss: row?.average_weight_loss,
  //     };

  //     if (row?.days_from_cut != null && row.days_from_cut !== "-") {
  //       existing.totalDays += Number(row.days_from_cut);
  //       existing.count += 1;
  //     }

  //     grouped.set(plantId, existing);
  //   });

  //   return Array.from(grouped.values()).map((item) => {
  //     const cultivar = item.cultivar || "-";
  //     const label = cultivar;
  //     return {
  //       plantId: item.plantId,
  //       cultivar,
  //       label,
  //       averageDays: item.count > 0 ? Number(item.totalDays / item.count) : 0,
  //       averageWeightLoss: item?.average_weight_loss,
  //     };
  //   });
  // }, [rows]);

  const chartData = useMemo(() => {
    if (!rows?.length) return [];

    return rows
      .filter((r) => r.days_from_cut != null && r.days_from_cut !== "-")
      .map((r) => ({
        days_from_cut: Number(r.days_from_cut),
        average_weight_loss: Number(r.average_weight_loss ?? 0),
        plantId: r?.plant_id || r?.clone_id || r?.id || r?._id || r?.cultivar,
        cultivar: r?.cultivar || "-",
      }));
  }, [rows]);

  // const CustomGroupedTicks = (data) => (props) => {
  //   // console.log("data ==> ", { data, props });
  //   const { x, y, payload, index } = props;

  //   const day = payload.value;
  //   const currentCultivar = data[index].cultivar;

  //   const prevCultivar = index > 0 ? data[index - 1].cultivar : null;
  //   const nextCultivar =
  //     index < data.length - 1 ? data[index + 1].cultivar : null;

  //   const isGroupStart = currentCultivar !== prevCultivar;

  //   return (
  //     <g transform={`translate(${x},${y})`}>
  //       {/* Day number */}
  //       <text textAnchor="middle" dy={10} fontSize="12">
  //         {day}
  //       </text>
  //       {isGroupStart && (
  //         <text
  //           x={0}
  //           y={32}
  //           textAnchor="end"
  //           fontSize="12"
  //           transform="rotate(-45)"
  //         >
  //           {currentCultivar}
  //         </text>
  //       )}
  //     </g>
  //   );
  // };

  const CustomGroupedTicks = (data) => {
    const groups = {};
    data.forEach((item, i) => {
      if (!groups[item.cultivar]) groups[item.cultivar] = [];
      groups[item.cultivar].push(i);
    });

    return (props) => {
      const { x, y, payload, index } = props;

      const day = payload.value;
      const cultivar = data[index].cultivar;
      const indexes = groups[cultivar];

      const middleIndex = indexes[Math.floor(indexes.length / 3)];

      const isMiddle = index === middleIndex;

      return (
        <g transform={`translate(${x},${y})`}>
          <text textAnchor="middle" dy={10} fontSize="12">
            {day}
          </text>
          {isMiddle && (
            <text
              dy={20}
              x={0}
              y={32}
              textAnchor="middle"
              fontSize="12"
              transform={`rotate(-45,0,35)`}
            >
              {cultivar}
            </text>
          )}
        </g>
      );
    };
  };

  const columns = [
    {
      field: "cultivar",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params.row?.cultivar || "-"}</div>
      ),
    },
    {
      field: "days_from_cut",
      headerName: "Days From Cut",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.days_from_cut ?? "-"}
        </div>
      ),
    },
    {
      field: "average_weight_loss",
      headerName: "Average Weight Loss",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.average_weight_loss ?? "-"}
        </div>
      ),
    },
    // {
    //   field: "cut_date",
    //   headerName: "Cut Date",
    //   flex: 1,
    //   minWidth: 160,
    //   renderCell: (params) => (
    //     <div style={{ padding: "10px" }}>
    //       {params.row?.cut_date
    //         ? dayjs(params.row.cut_date).format("MM-DD-YYYY")
    //         : "-"}
    //     </div>
    //   ),
    // },
  ];

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const getColumns = (selectedOption) => [
    {
      field: "cultivar",
      headerName: `${capitalize(selectedOption)} Name`,
      flex: 1,
      minWidth: 190,
      headerClassName: "headerclass",
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params.row?.cultivar || "-"} </div>
      ),
    },
    {
      field: "days_from_cut",
      headerName: "Days From Cut",
      flex: 1,
      minWidth: 130,
      headerClassName: "headerclass",
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.days_from_cut ?? "-"}
        </div>
      ),
    },
    {
      field: "average_weight_loss",
      headerName: "Average Weight Loss",
      flex: 1,
      minWidth: 180,
      headerClassName: "headerclass",
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.average_weight_loss ?? "-"}
        </div>
      ),
    },
    // {
    //   field: "cut_date",
    //   headerName: "Cut Date",
    //   flex: 1,
    //   minWidth: 160,
    //   renderCell: (params) => (
    //     <div style={{ padding: "10px" }}>
    //       {params.row?.cut_date
    //         ? dayjs(params.row.cut_date).format("MM-DD-YYYY")
    //         : "-"}
    //     </div>
    //   ),
    // },
  ];

  // const historyColumns = [
  //   {
  //     field: "cultivar",
  //     headerName: "Cultivar Name",
  //     flex: 1,
  //     minWidth: 190,
  //     renderCell: (params) => (
  //       <div style={{ padding: "10px" }}>{params?.row?.cultivar || "-"}</div>
  //     ),
  //   },
  // {
  //   field: "clone_id",
  //   headerName: "Clone ID",
  //   flex: 1,
  //   minWidth: 140,
  //   renderCell: (params) => (
  //     <div style={{ padding: "10px" }}>{params?.row?.clone_id || "-"}</div>
  //   ),
  // },
  // {
  //   field: "first_partially_rooted",
  //   headerName: "Date Of Initial Rooting",
  //   flex: 1,
  //   minWidth: 170,
  //   renderCell: (params) => (
  //     <div style={{ padding: "10px" }}>
  //       {params.row?.first_partially_rooted
  //         ? dayjs(params.row.first_partially_rooted).format("MM-DD-YYYY")
  //         : "-"}
  //     </div>
  //   ),
  // },
  // {
  //   field: "first_fully_rooted",
  //   headerName: "Date Of Fully Rooted",
  //   flex: 1,
  //   minWidth: 170,
  //   renderCell: (params) => (
  //     <div style={{ padding: "10px" }}>
  //       {params.row?.first_fully_rooted
  //         ? dayjs(params.row.first_fully_rooted).format("MM-DD-YYYY")
  //         : "-"}
  //     </div>
  //   ),
  // },
  // ];

  const clusterColumns = [
    {
      field: "cultivar",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params?.row?.cultivar || "-"}</div>
      ),
    },
    {
      field: "cluster_id",
      headerName: "Cluster ID",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "date",
      headerName: "Rockwool Date",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        return (
          <div style={{ padding: "10px" }}>
            {moment(params?.row?.date).format("MM-DD-YYYY")}
          </div>
        );
      },
    },
    {
      field: "before_weight",
      headerName: "Before Weight",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "after_weight",
      headerName: "After Weight",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "threshold_value",
      headerName: "Threshold Value",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "cuts",
      headerName: "No. of Cuts",
      flex: 1,
      minWidth: 190,
    },
  ];

  const cloneColumns = [
    {
      field: "cultivar",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params?.row?.cultivar || "-"}</div>
      ),
    },
    {
      field: "clone_id",
      headerName: "Clone ID",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params?.row?.clone_id || "-"}</div>
      ),
    },
    {
      field: "date",
      headerName: "Rockwool Date",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        return (
          <div style={{ padding: "10px" }}>
            {moment(params?.row?.date).format("MM-DD-YYYY")}
          </div>
        );
      },
    },
    {
      field: "before_weight",
      headerName: "Before Weight",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "after_weight",
      headerName: "After Weight",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "threshold_value",
      headerName: "Threshold Value",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "rooting_status",
      headerName: "Rooting Status",
      flex: 1,
      minWidth: 190,
    },
  ];

  const historyColumns = typeFilter === "clone" ? cloneColumns : clusterColumns;

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="maintenance-view">
        <div className="page-title">
          <div className="title-wrap">
            <h1>Average Weight Loss Report For Clusters and Clones</h1>
          </div>
        </div>

        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
          <div className="header_search-wrap">
            <div className="header_search_bar common_content d-flex">
              <div
                className="input-group input_group"
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={pendingStartDate ? dayjs(pendingStartDate) : null}
                    disableFuture
                    onChange={(newStartDate) => {
                      setPendingStartDate(
                        newStartDate
                          ? dayjs(newStartDate).format("YYYY-MM-DD")
                          : ""
                      );
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px" }}
                  />
                  <DatePicker
                    label="End Date"
                    value={pendingEndDate ? dayjs(pendingEndDate) : null}
                    disableFuture
                    minDate={
                      pendingStartDate && dayjs(pendingStartDate).isValid()
                        ? dayjs(pendingStartDate)
                        : undefined
                    }
                    onChange={(newEndDate) => {
                      setPendingEndDate(
                        newEndDate ? dayjs(newEndDate).format("YYYY-MM-DD") : ""
                      );
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px" }}
                  />
                </LocalizationProvider>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                style={{ minWidth: 120, marginLeft: 8, fontSize: "16px" }}
                onClick={() => {
                  setStartDate(pendingStartDate);
                  setEndDate(pendingEndDate);
                  setPage(0);
                }}
              >
                Get Details
              </button>
            </div>
          </div>
        </div>

        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
          <div className="input-group input_group">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(0);
                }}
                style={{ backgroundColor: "white" }}
              >
                <MenuItem value="clone">Clone</MenuItem>
                <MenuItem value="cluster">Cluster</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="header_search-wrap">
            <div className="header_search_bar common_content">
              <div
                className="input-group input_group"
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                <button className="btn search-btn search_btn">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 21 21"
                    fill="none"
                  >
                    <circle
                      cx="9.98856"
                      cy="9.98856"
                      r="8.98856"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.2402 16.7071L19.7643 20.222"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  type="search"
                  className="form-control form_control"
                  placeholder="Search..."
                  aria-label="Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            marginTop: "20px",
          }}
        >
          <div>
            <div
              style={{
                height: 480,
                width: "630px",
              }}
            >
              <DataGrid
                rows={rows}
                getRowId={(r) =>
                  r.id ||
                  r._id ||
                  `${r.cultivar || "cultivar"}-${r.clone_id || "clone"}-${
                    r.first_partially_rooted || Math.random()
                  }`
                }
                columns={getColumns(typeFilter)}
                loading={loading}
                rowCount={rowCount}
                pageSizeOptions={[15, 50, 100]}
                paginationModel={{ pageSize, page }}
                paginationMode="server"
                onPaginationModelChange={({ page, pageSize }) => {
                  setPage(page);
                  setPageSize(pageSize);
                }}
                getRowHeight={() => "auto"}
                sx={{
                  border: "1px solid #E0E0E0",
                  [`& .${gridClasses.cell}`]: { py: 0.5 },
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                onRowClick={(params) => {
                  const plantIdentifier = params?.row?.plant_id;
                  const daysFromCuts = params?.row?.days_from_cut;
                  if (plantIdentifier) {
                    setSelectedPlantId(plantIdentifier);
                    setSelectedDaysFromCut(daysFromCuts);
                    setHistoryPage(0);
                    if (historySearch) {
                      setHistorySearch("");
                    }
                    scrollToHistorySection();
                  }
                }}
              />
            </div>
          </div>

          <div>
            <div
              style={{
                backgroundColor: "#fff",
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
                padding: "20px",
                height: 480,
                minWidth: "950px",
                minHeight: "400px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h3
                style={{
                  marginBottom: "20px",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                No. of Days from Cut Date
              </h3>
              {/* {chartData.length > 0 ? (
                <LineChart
                  width={600}
                  height={400}
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                  onClick={(chartEvent) => {
                    const payload =
                      chartEvent?.activePayload && chartEvent.activePayload[0];
                    const plantIdFromPoint = payload?.payload?.plantId;
                    if (plantIdFromPoint) {
                      setSelectedPlantId(plantIdFromPoint);
                      setHistoryPage(0);
                      if (historySearch) {
                        setHistorySearch("");
                      }
                      scrollToHistorySection();
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    style={{ fontSize: "12px" }}
                  />
                  <YAxis
                    label={{
                      value: "Average Weight Loss",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="averageDays"
                    name="Avg. Days from Cut Date (per plant)"
                    stroke="#0066CC"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "600px",
                    height: "400px",
                    color: "#999",
                  }}
                >
                  No data available for chart
                </div>
              )} */}

              {loading ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={48} />
                </div>
              ) : chartData.length > 0 ? (
                <LineChart
                  width={800}
                  height={400}
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 90,
                  }}
                  style={{ cursor: "pointer" }}
                  onClick={(chartEvent) => {
                    const payload =
                      chartEvent?.activePayload && chartEvent.activePayload[0];
                    const plantIdFromPoint = payload?.payload?.plantId;
                    const daysFromCuts = payload?.payload?.days_from_cut;
                    if (plantIdFromPoint) {
                      setSelectedPlantId(plantIdFromPoint);
                      setSelectedDaysFromCut(daysFromCuts);
                      setHistoryPage(0);
                      if (historySearch) {
                        setHistorySearch("");
                      }
                      scrollToHistorySection();
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="days_from_cut"
                    // angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    style={{ fontSize: "12px", top: "12px" }}
                    tick={CustomGroupedTicks(chartData)}
                  />
                  <YAxis
                    label={{
                      value: "Average of Weight Loss",
                      angle: -90,
                      textAnchor: "middle",
                    }}
                  />

                  <Tooltip
                    content={<ChartCustomTooltip />}
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ bottom: 10 }}
                  />

                  {/* Line showing weight loss across days */}
                  <Line
                    type="monotone"
                    dataKey="average_weight_loss"
                    name="Cultivar Name and No. of Days from CutDate"
                    stroke="#0066CC"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "600px",
                    height: "400px",
                    color: "#999",
                  }}
                >
                  No data available for chart
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "32px" }} ref={historySectionRef}>
          <div className="page-title pb-1 pt-2">
            <div className="title-wrap">
              <h1>Rockwool Information</h1>
            </div>
          </div>
          <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-1">
            <div className="header_search-wrap">
              <div className="header_search_bar common_content">
                <div className="input-group input_group">
                  <button className="btn search-btn search_btn">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 21 21"
                      fill="none"
                    >
                      <circle
                        cx="9.98856"
                        cy="9.98856"
                        r="8.98856"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16.2402 16.7071L19.7643 20.222"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <input
                    type="search"
                    className="form-control form_control"
                    placeholder="Search..."
                    aria-label="Search"
                    value={historySearch}
                    onChange={(e) => {
                      setHistorySearch(e.target.value);
                      setHistoryPage(0);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div
              style={{
                height: `${historyRows?.length > 0 ? "" : "200px"}`,
                width: "100%",
                marginTop: 12,
              }}
            >
              <DataGrid
                rows={historyRows}
                getRowId={(r) =>
                  r.id ||
                  r._id ||
                  `${r.cultivar || "cultivar"}-${r.clone_id || "clone"}-${
                    r.first_partially_rooted || Math.random()
                  }`
                }
                columns={historyColumns}
                loading={historyLoading}
                rowCount={historyRowCount}
                pageSizeOptions={[15, 50, 100]}
                paginationModel={{
                  pageSize: historyPageSize,
                  page: historyPage,
                }}
                paginationMode="server"
                onPaginationModelChange={({ page, pageSize }) => {
                  setHistoryPage(page);
                  setHistoryPageSize(pageSize);
                }}
                getRowHeight={() => "auto"}
                getRowClassName={(params) => {
                  return params.row?.highlight_red ? "highlight-red-row" : "";
                }}
                sx={{
                  border: "1px solid #E0E0E0",
                  [`& .${gridClasses.cell}`]: { py: 0.5 },
                }}
                autoHeight
                showCellVerticalBorder
                showColumnVerticalBorder
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RockwoolRootingHistory;
